'use client';
import { MouseEvent, useEffect, useState } from 'react';

import EmptyStarIcon from '../Icons/EmptyStar';
import GalleryIcon from '../Icons/Gallery';
import { Village, VillageFeature } from '@/services/types';
import { useCurrency } from '@/contexts/currencyContext';
import { VillaCoverMedia } from '@/types/Villa';
import { getVillageCoverMedia } from '@/utils/village';

export type WishList = {
  id: number;
  title: string;
  sleeps: number;
  bedrooms: number;
  bathrooms: number;
  city: string;
  media: string;
  coverMedia?: VillaCoverMedia;
  prices: {
    originalPrice: string;
    ownerPrice: string;
    currency: string;
  };
  zone: {
    code: string;
    enabled: boolean;
  };
  villageMainFeatures: VillageFeature[];
  slug: string;
};

type Props = {
  village: Village;
  onGalleryClick: (village: Village) => void;
  wishList: WishList[];
  setWishList: (data: WishList[]) => void;
};

export default function VillageCardOptions({
  village,
  onGalleryClick,
  wishList,
  setWishList,
}: Props) {
  const [inWishList, setInWishList] = useState(false);

  const villageMainFeatures = village.features?.filter(({ feature }) => {
    return feature?.type ? feature.type === 'main_feature' : false;
  });

  const { currency } = useCurrency();

  const coverMedia = getVillageCoverMedia(village.media);
  const villageMedia = coverMedia?.mediaFilters?.find(
    (img) => img.name === 'medium_3-2',
  )?.url;

  const wishListInfos: WishList = {
    id: village.id,
    title: village.title,
    sleeps: village.sleeps,
    bedrooms: village.bedrooms,
    bathrooms: village.bathrooms,
    city: village.city,
    media: villageMedia ? villageMedia : '',
    coverMedia,
    prices: {
      originalPrice: village.calculatedPrice.toString(),
      ownerPrice: village.calculatedPrice.toString(),
      currency: currency.value,
    },
    zone: {
      code: village.zone.code,
      enabled: true,
    },
    villageMainFeatures: villageMainFeatures,
    slug: village.slug,
  };

  function handleGalleryOptionClick(e: MouseEvent) {
    e.preventDefault();
    onGalleryClick(village);
  }

  function handleVillageWishList(e: MouseEvent) {
    e.preventDefault();
    let newWishList: WishList[] = [];

    if (!inWishList) {
      newWishList = [...wishList, wishListInfos];
    } else {
      newWishList = wishList.filter(
        (saved) => saved.title !== wishListInfos.title,
      );
    }
    setWishList(newWishList);
  }

  useEffect(() => {
    const isSaved = wishList.find(
      (savedVillage) => savedVillage.title === village.title,
    );
    if (isSaved) {
      setInWishList(true);
    } else {
      setInWishList(false);
    }
    //eslint-disable-next-line
  }, [wishList]);

  return (
    <ul className="float-right list-none py-[10px] ml-2 flex flex-row space-x-4">
      <li className="hidden desktop:inline-block">
        <button
          onClick={handleGalleryOptionClick}
          className="hidden desktop:flex items-center justify-center w-full h-full relative cursor-pointer appearance-none bg-transparent border-0">
          <GalleryIcon className="w-8 h-8 fill-current text-white " />
        </button>
      </li>
      <li className="inline-block group-[.is-absolute]:rounded-full group-[.is-absolute]:h-[45px] group-[.is-absolute]:w-[45px] z-[15]">
        <button
          id={`${village.slug}-favorite`}
          aria-label={`Favorite ${village.title} village`}
          tabIndex={-1}
          onClick={handleVillageWishList}
          className="relative cursor-pointer flex items-center justify-center w-full h-full appearance-none bg-transparent border-0">
          {inWishList ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8 group-[.is-absolute]:h-6 group-[.is-absolute]:w-6 fill-current text-white"
              viewBox="0 0 24 24">
              <path fill="none" d="M0 0h24v24H0z" data-name="Rectangle 4" />
              <path
                stroke="currentColor"
                strokeMiterlimit="10"
                strokeWidth="1.5"
                d="m12 18-5.878 3.09 1.123-6.545L2.489 9.91l6.572-.955L12 3l2.939 5.955 6.572.955-4.755 4.635 1.123 6.545Z"
              />
            </svg>
          ) : (
            <EmptyStarIcon className="w-8 h-[32px] fill-current text-white" />
          )}
        </button>
      </li>
    </ul>
  );
}
